import { Fragment } from 'react';
import {
  Routes,
  Route,
  Navigate,
  NavigateFunction,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AppLayout, PublicRouteLayout, ProtectedRouteLayout } from './layouts';
import {
  LeadDetailsPage,
  LeadsUnderManagementPage,
  LoginPage,
  DashboardPage,
  LeadsClosedPage,
  LeadsDroppedPage,
  HelpPage,
  ActionLinkPage,
  ProfilePage,
  ProfilesListPage,
  QuotesPage,
  PagesListPage,
  NavigationMenuPage,
  NavigationFooterPage,
  DropRequestsPage,
  TeamsPage,
  TeamsDetailPage,
  TeamGroupDetailPage,
  TeamsManagerPage,
  DashboardDeepDiveTeamPage,
  DashboardFatturatoPersoPage,
  CappelloParlantePage,
  BankInfosListPage,
  DashboardAdminPage,
  TasksPage,
  GoogleCalendarCallbackPage,
  ExternalLeadsPage,
  ConsultationRequestsPage,
  ManagedLoanListPage,
  ManagedLoanDetailPage,
  ManagedCdqListPage,
  ManagedCdqDetailPage,
  LeadTassoNewPage,
} from './pages';
import ProtectedPageLayout from './layouts/ProtectedPageLayout/ProtectedPageLayout';
import AdminRouteGuard from './layouts/AdminRouteGuard/AdminRouteGuard';
import {
  ManagedLeadProduct,
  NON_MORTGAGE_MANAGED_LEAD_PRODUCTS,
} from './config/managedLeadNavigation';
import { ManagedLeadCategory } from './services/api/managed-leads/managedLeads';

const managedLeadCategoryByRoute: Record<
  'in-progress' | 'won' | 'dropped',
  ManagedLeadCategory
> = {
  'in-progress': 'in_progress',
  won: 'won',
  dropped: 'dropped',
};

/**
 * Pages of the products served by Tasso Quinto. A product without an entry
 * registers no route at all, so it can never fall back to another product's
 * pages and data.
 */
const managedLeadProductPages: Partial<
  Record<
    ManagedLeadProduct['id'],
    {
      List: (props: { category: ManagedLeadCategory }) => JSX.Element;
      Detail: () => JSX.Element;
    }
  >
> = {
  loans: { List: ManagedLoanListPage, Detail: ManagedLoanDetailPage },
  cdq: { List: ManagedCdqListPage, Detail: ManagedCdqDetailPage },
};

// custom history object to allow navigation outside react components
export const history: {
  navigate: null | NavigateFunction;
  // @ts-expect-error - Location type is not defined in react-router-dom
  location: null | Location<unknown>;
} = {
  navigate: null,
  location: null,
};

export function Router() {
  // init custom history object to allow navigation from
  // anywhere in the React app (inside or outside components)
  history.navigate = useNavigate();
  history.location = useLocation();

  return (
    <Routes>
      <Route path="/" element={<AppLayout />}>
        <Route index element={<Navigate to="lead" replace />} />

        {/*PUBLIC ROUTES */}
        <Route element={<PublicRouteLayout />}>
          <Route path="login" element={<LoginPage />} />
        </Route>

        {/*PROTECTED ROUTES */}
        <Route element={<ProtectedRouteLayout />}>
          <Route
            path="google-calendar-callback"
            element={<GoogleCalendarCallbackPage />}
          />
          <Route element={<ProtectedPageLayout />}>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route
              path="dashboard/deep-dive"
              element={<DashboardDeepDiveTeamPage />}
            />
            <Route
              path="dashboard/fatturato-perso"
              element={<DashboardFatturatoPersoPage />}
            />
            <Route path="lead" element={<LeadsUnderManagementPage />} />
            <Route path="lead/:id" element={<LeadDetailsPage />} />
            <Route path="lead-closed" element={<LeadsClosedPage />} />
            <Route path="lead-dropped" element={<LeadsDroppedPage />} />
            {NON_MORTGAGE_MANAGED_LEAD_PRODUCTS.map((product) => {
              const pages = managedLeadProductPages[product.id];
              if (!pages) return null;

              return (
                <Fragment key={product.id}>
                  {product.categories.map((category) => (
                    <Route
                      key={category.id}
                      path={category.path.slice(1)}
                      element={
                        <pages.List
                          category={managedLeadCategoryByRoute[category.id]}
                        />
                      }
                    />
                  ))}
                  <Route
                    path={product.detailPath.slice(1)}
                    element={<pages.Detail />}
                  />
                </Fragment>
              );
            })}
            <Route path="help" element={<HelpPage />} />
            <Route path="do/:hash" element={<ActionLinkPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="tasks" element={<TasksPage />} />

            <Route path="admin">
              <Route path="dashboard" element={<DashboardAdminPage />} />
              <Route path="navigation-menu" element={<NavigationMenuPage />} />
              <Route
                path="navigation-footer"
                element={<NavigationFooterPage />}
              />
              <Route path="pages" element={<PagesListPage />} />
              <Route path="profiles" element={<ProfilesListPage />} />
              <Route path="bank-infos" element={<BankInfosListPage />} />

              <Route path="quotes" element={<QuotesPage />} />
              <Route path="drop" element={<DropRequestsPage />} />
              <Route
                path="cappelloparlante"
                element={<CappelloParlantePage />}
              />

              <Route path="external-leads" element={<ExternalLeadsPage />} />
              <Route
                path="external-leads/:product"
                element={<ExternalLeadsPage />}
              />

              <Route
                path="consultation-requests"
                element={<ConsultationRequestsPage />}
              />

              <Route
                path="lead-tasso/new"
                element={
                  <AdminRouteGuard>
                    <LeadTassoNewPage />
                  </AdminRouteGuard>
                }
              />

              <Route path="teams/management" element={<TeamsPage />} />
              <Route path="teams/managers" element={<TeamsManagerPage />} />
              <Route path="teams/:teamId?" element={<TeamsDetailPage />} />
              <Route
                path="teams/:teamId/group/:groupId"
                element={<TeamGroupDetailPage />}
              />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export default Router;
